// hooks/market/useProduct.ts
//
// Récupère le détail complet d'un produit par son id, pour l'écran
// "Voir plus" (app/(tabs)/market/produits/[id].tsx) et pour tout futur
// usage dans boutiques/[slug].tsx. Appelle le vrai backend :
//   POST {API_BASE_URL}/market   body: { action: 'get-product', access_token, product_id }
// (voir backend/handleMarket.ts → getProduct)

import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';
import type { MarketFeedProduct } from '@/hooks/market/useMarketFeed';

// Le détail complet partage la même forme que MarketFeedProduct (même
// vue SQL market_marketplace_feed), avec en plus la description longue,
// pas retournée dans le feed pour garder les payloads de liste légers.
export type MarketProductDetail = MarketFeedProduct & {
  description: string | null;
  sku: string | null;
  learn_more_url: string | null;
  call_phone: string | null;
};

export function useProduct(productId: string | undefined) {
  const [product, setProduct] = useState<MarketProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const fetchProduct = useCallback(async () => {
    if (!productId) return;

    setLoading(true);
    setError(null);
    setNotFound(false);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'get-product',
          access_token: token,
          product_id:   productId,
        }),
      });

      const data = await res.json();

      if (res.status === 404) {
        setNotFound(true);
        return;
      }

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de charger le produit');
        return;
      }

      setProduct(data.product);
    } catch (err) {
      console.error('[useProduct]', err);
      setError('network_error');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  return { product, loading, error, notFound, refresh: fetchProduct };
}
